/**
 * NavbarIconButton Component
 *
 * Shared button wrapper for navbar icons using lucide-react.
 * Ensures consistent sizing, stroke width, and hover behavior across all navbar icons.
 *
 * Container: 31.5px × 31.5px
 * Icon size: 18px × 18px
 * Stroke width: 1.5
 */
"use client";

import { forwardRef } from "react";

import { type LucideIcon } from "lucide-react";

// Shared icon styling constants
export const NAVBAR_ICON_STYLES = {
  containerSize: 31.5,
  iconSize: 16,
  strokeWidth: 1.5,
} as const;

interface NavbarIconButtonProps {
  icon: LucideIcon;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  active?: boolean;
  ariaLabel: string;
  testId?: string;
  className?: string;
  children?: React.ReactNode; // For overlays like badges
}

export const NavbarIconButton = forwardRef<HTMLButtonElement, NavbarIconButtonProps>(
  function NavbarIconButton(
    {
      icon: Icon,
      onClick,
      onMouseEnter,
      onMouseLeave,
      active = false,
      ariaLabel,
      testId,
      className = "",
      children,
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={`relative flex items-center justify-center rounded-full transition-opacity hover:opacity-80 ${className}`}
        style={{
          width: `${NAVBAR_ICON_STYLES.containerSize}px`,
          height: `${NAVBAR_ICON_STYLES.containerSize}px`,
          backgroundColor: active ? "#E9EBEF" : "transparent",
        }}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        <Icon
          className="relative"
          style={{
            width: `${NAVBAR_ICON_STYLES.iconSize}px`,
            height: `${NAVBAR_ICON_STYLES.iconSize}px`,
          }}
          strokeWidth={NAVBAR_ICON_STYLES.strokeWidth}
        />
        {children}
      </button>
    );
  }
);
